/**
 * Axios instance with interceptors for API requests.
 * 包含攔截器的 Axios 實例，用於處理 API 請求。
 */
import axios from "axios";
import { clearSession } from "@/shared/store/slices/userSlice";
import { setNetworkError, setSuspensionNotice } from "@/shared/store/slices/appSlice";

/**
 * store 由 store.js 在建立完成後注入，這個模組不 import 它。
 *
 * 反過來 import 會形成 slice → axios → store → slice 的循環：實測在「有模組比
 * store.js 更早 import 到任何一個 slice」的載入順序下，store.js 會拿到尚未初始化的
 * reducer 而拋 ReferenceError（整站白畫面）。目前不炸只是因為 main.jsx 先 import
 * store 把順序鎖死，而那是碰不得的隱性前提。改成注入後，依賴方向只剩單向。
 *
 * 注入發生在 store.js 的模組初始化階段（同步），早於任何元件發出請求。
 */
let store = null;
let refetchIdolArray = null;

/**
 * refetchIdolArray 同樣由 store.js 注入：getIdolArray 定義在 idolSlice，而 idolSlice
 * import 本模組，直接 import 會形成 axios ↔ idolSlice 循環。
 */
export const setAxiosStore = (injectedStore, handlers) => {
  store = injectedStore;
  refetchIdolArray = handlers.refetchIdolArray;
};

/**
 * IDOL_3044 觸發的偶像列表重抓（single-flight）：同一頁多支 API 同時回傳時只送一次。
 * 重抓回來後由 selectIsIdolLocked 判斷目前偶像是否停權，這裡不直接鎖定——
 * IDOL_3044 也可能是操作別的偶像（例如 switch-check 切到停權偶像），不代表目前偶像被停權。
 */
let isRefetchingIdolArray = false;
const refetchIdolArrayOnce = () => {
  if (isRefetchingIdolArray) return;
  isRefetchingIdolArray = true;
  refetchIdolArray().finally(() => {
    isRefetchingIdolArray = false;
  });
};

/**
 * 多媒體上傳（multipart）專用的逾時上限，由下方 request interceptor 對所有
 * FormData 請求自動套用，呼叫端不需要（也不應該）自己帶。
 *
 * 一般請求沿用下方的 180 秒；但上傳的 payload 可達數十 MB，慢速上行時
 * 180 秒會在後端回應前就先逾時，使用者拿到的是「請求逾時」，
 * 而不是後端真正要說的 HTTP_413（檔案過大）。
 * 放寬到 10 分鐘讓後端的狀態碼有機會回來；仍是有限值，請求真的 hang 住時還是有出口。
 */
const UPLOAD_TIMEOUT_MS = 600000;

const Axios = axios.create({
  baseURL: import.meta.env.VITE_REACT_APP_API_URL,
  // 逾時上限：避免請求 hang 住時使用者被鎖在 loading 中沒有出口。
  // 180 秒遠高於最慢的一般 API（檢舉約 7 秒），正常請求不會誤判；
  // multipart 上傳不適用此值，由 interceptor 換成 UPLOAD_TIMEOUT_MS。
  // 真的逾時會走下方攔截器的 TIMEOUT_ERROR 分支，由呼叫端的 StatusModal 顯示，
  // 使用者留在原畫面（不會被整頁 ServerErrorPage 洗掉編輯中的內容）。
  timeout: 180000,
  // 逾時改回報 ETIMEDOUT，與「請求被中止」的 ECONNABORTED 區分開來（預設兩者都是 ECONNABORTED）
  transitional: { clarifyTimeoutError: true },
});

/**
 * 頁面正在離開（整頁跳轉、重新整理、點了整頁連結）。
 *
 * 離開時舊頁面還沒回來的請求會被瀏覽器中止：WebKit 觸發 error（被當成斷線 → 整頁 ServerErrorPage），
 * Firefox 觸發 abort。例如選偶像後 window.location.href 整頁重載，重載完成前舊頁面會閃出錯誤畫面。
 * 這類失敗不是真的斷線，畫面也即將被換掉，所以標記 NETWORK_ERROR（呼叫端標準寫法不會顯示 modal）
 * 但不觸發 ServerErrorPage。
 *
 * - beforeunload／markPageUnloading：導航才剛開始，仍可能被取消（使用者按停止、目標回 204），
 *   只在之後一小段時間內有效，否則導航取消後真的斷線、逾時都會被吞掉。
 *   已在送的請求實測在 beforeunload 後十幾毫秒內就失敗；時間窗取 5 秒，是為了再涵蓋：
 *   新頁面回應很慢時，使用者在等待期間又在舊頁面發出的請求（WebKit 會立刻失敗），
 *   以及主執行緒忙碌（低階手機）導致失敗 callback 延後執行（Firefox）。
 *   代價是導航被取消後的 5 秒內，真的斷線（以及極少數同時發生的逾時）完全沒有提示：
 *   不顯示 ServerErrorPage，呼叫端標準寫法也不跳 modal（之後恢復正常）
 * - pagehide：確定離開；從 bfcache 返回（pageshow persisted）時頁面仍是同一個，要復原
 */
const UNLOAD_ABORT_WINDOW_MS = 5000;
let unloadStartedAt = null;
let isPageHidden = false;

/**
 * 程式主動整頁跳轉（window.location.href）前呼叫。
 * iOS Safari 不一定會觸發 beforeunload，靠這裡補上標記。
 */
export const markPageUnloading = () => {
  unloadStartedAt = Date.now();
};

const isPageUnloading = () =>
  isPageHidden || (unloadStartedAt !== null && Date.now() - unloadStartedAt < UNLOAD_ABORT_WINDOW_MS);

window.addEventListener('beforeunload', markPageUnloading);
window.addEventListener('pagehide', () => {
  isPageHidden = true;
});
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    isPageHidden = false;
    unloadStartedAt = null;
  }
});

// Request interceptor for attaching auth token
// 添加請求攔截器附加驗證 Token
Axios.interceptors.request.use(
  config => {
    const token = store.getState().user.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 記下送出時的 token：回應回來時用來判斷是不是目前這次登入發出的請求
    config.sessionToken = token;
    // multipart 上傳（貼文、世界觀、偶像資料、聊天室圖片）一律改用較寬鬆的逾時。
    // 集中在此判斷，新增上傳點時不會漏掉。
    if (config.data instanceof FormData) {
      config.timeout = UPLOAD_TIMEOUT_MS;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling token errors and HTTP errors
// 處理 Token 錯誤和 HTTP 錯誤的響應攔截器
Axios.interceptors.response.use(
  response => {
    const statusCode = response.data?.status_code;
    // 回應是否屬於目前這次登入：登出後立刻重新登入時，登出前送出的請求晚回來會帶著舊 token 的
    // SYSTEM_1004，不能因此清掉新的登入狀態。登出後 token 為 null，也一併擋掉重複處理
    const currentToken = store.getState().user.token;
    const isCurrentSession = Boolean(currentToken) && response.config.sessionToken === currentToken;
    // 登入驗證過期：清空整份登入狀態（與登出相同），避免暱稱、聊天室列表、通知等個人資料留在 localStorage。
    // 清空後 token 為 null，同一頁多支 API 同時回傳只處理一次
    if (statusCode === "SYSTEM_1004" && isCurrentSession) {
      store.dispatch(clearSession());
    }
    // 帳號停權：清空登入狀態並通知，同樣只處理目前這次登入的回應
    if (statusCode === "USER_2034" && isCurrentSession) {
      store.dispatch(clearSession());
      store.dispatch(setSuspensionNotice('account'));
    }
    // 偶像停權或已刪除：重抓偶像列表，交給 selectIsIdolLocked 判斷是否為目前偶像
    if (statusCode === "IDOL_3044" && store.getState().user.token) {
      refetchIdolArrayOnce();
    }
    return response;
  },
  error => {
    if (error.response) {
      // HTTP 錯誤：伺服器有回應但回傳錯誤狀態碼
      const status = error.response.status;
      error.httpErrorCode = `HTTP_${status}`;
    } else if (isPageUnloading()) {
      // 頁面正在離開，請求被瀏覽器中止（見 isPageUnloading 說明）：不觸發 ServerErrorPage
      error.httpErrorCode = 'NETWORK_ERROR';
    } else if (error.code === 'ETIMEDOUT') {
      // 逾時：請求送得出去、伺服器只是還沒回（慢查詢、大檔上傳），不是全站斷線。
      // 不設 isNetworkError，避免 App.jsx 用整頁 ServerErrorPage 蓋掉畫面——
      // 那會連同使用者正在編輯的貼文／訊息／表單一起洗掉，而重試按鈕是 reload，救不回來。
      // 改走各呼叫端既有的 StatusModal，使用者留在原畫面自行決定要不要重送。
      error.httpErrorCode = 'TIMEOUT_ERROR';
    } else {
      // 網路層錯誤：請求未送達伺服器（斷線、伺服器停止回應等），以及非離開頁面時被中止的請求（ECONNABORTED）
      error.httpErrorCode = 'NETWORK_ERROR';
      // 背景請求（config.isBackground，例如偶像上線狀態輪詢）不觸發整頁 ServerErrorPage：
      // 使用者沒有主動操作，剛從休眠喚醒、網路還沒恢復時打出去，不該洗掉正在編輯的內容。
      // 仍標記 NETWORK_ERROR，由呼叫端自行處理。
      const alreadyTriggered = store.getState().app.isNetworkError;
      if (!alreadyTriggered && !error.config?.isBackground) {
        store.dispatch(setNetworkError(true));
      }
    }
    return Promise.reject(error);
  }
);

export default Axios;
